import type { BookServerAction, BookServerActionKind } from './bookActionController'
import type { BookReviewScope } from './bookReviewQuery'
import type { DeckCard } from './types'

export interface BookActionWire {
  proposal_id: string
  group_id: string
  proposal_type: string
  action_kind: string
  enabled: boolean
  selected_proposal_ids?: readonly string[]
  also_books?: readonly string[]
  dependency_ids?: readonly string[]
  affected_fund_ids?: readonly string[]
  requires_confirmation?: boolean
  disabled_reasons?: readonly string[]
}

export function cardBookProposalIds(card: Pick<DeckCard, 'rows' | 'objects' | 'claims'>): string[] {
  const ids: string[] = []
  const add = (value: string | null | undefined) => {
    if (value && !ids.includes(value)) ids.push(value)
  }
  for (const row of [...(card.rows ?? []), ...(card.objects ?? [])]) add(row.proposal_id)
  for (const claim of card.claims ?? []) {
    add(claim.proposal_id)
    for (const proposalId of claim.also_books ?? []) add(proposalId)
  }
  return ids
}

export function ordinaryBookCard(
  card: Pick<DeckCard, 'stage' | 'commitment'>,
  groupId: string | null | undefined,
  proposalIds: readonly string[]
): boolean {
  return card.stage === 'book' && !!groupId && proposalIds.length > 0
}

export function capturedBookScope(fundIds: readonly string[] | undefined): BookReviewScope {
  return fundIds?.length === 1 ? { fundId: fundIds[0] } : {}
}

export function reviewBindingScope(
  bindingScope: Readonly<Record<string, string | null>> | undefined,
  fallback: BookReviewScope = {}
): BookReviewScope {
  const fundId = bindingScope?.fund_id ?? bindingScope?.fundId ?? null
  if (fundId) return { fundId }
  const clientId = bindingScope?.client_id ?? bindingScope?.clientId ?? null
  if (clientId) return { clientId }
  return fallback
}

function actionKind(value: string): BookServerActionKind {
  if (value === 'book') return 'book'
  if (value === 'book_plug') return 'plug'
  if (value === 'book_all') return 'book_all'
  if (value === 'pending') return 'pending'
  return 'blocked'
}

function actionLabel(action: BookActionWire): string {
  if (action.action_kind === 'book_plug') return 'Book provisional movement'
  if (action.action_kind === 'wait_for_first_position') return 'Waiting for first position'
  if (action.action_kind === 'bundled') return 'Bundled with first position'
  const kind = action.proposal_type.replace(/^(create|update)_/, '').replace(/_/g, ' ')
  return 'Book ' + kind
}

export function mapBookActionItems(actions: readonly BookActionWire[]): BookServerAction[] {
  return actions.map((action) => ({
    id: 'book:' + action.proposal_id,
    label: actionLabel(action),
    kind: actionKind(action.action_kind),
    proposal_id: action.proposal_id,
    selected_proposal_ids: action.selected_proposal_ids ?? [],
    also_books: action.also_books ?? [],
    disabled: !action.enabled,
    disabled_reason: action.disabled_reasons?.some((reason) =>
      reason.includes('nominal_citation_requires_review_decision')
    )
      ? 'Review the nominal citation. Booking as stated is limited to unchanged trades with no other unverified source details.'
      : action.disabled_reasons?.some((reason) => reason.startsWith('dependency_not_ready:'))
        ? 'Review the required earlier records first.'
        : (action.disabled_reasons?.join(' · ') ?? null),
  }))
}
